/**
 * Store Helper Functions
 * Utility functions for store/shop management
 */

import {
  Store as StoreIcon,
  LocalMall,
  Restaurant,
  Storefront,
  ShoppingBag,
  Face,
  Watch,
  MenuBook,
  Toys,
  Home,
  FitnessCenter,
  LocalPharmacy,
  TheaterComedy,
  Build,
  Category,
} from "@mui/icons-material";
import { StoreCategory, StoreCategoryConfig } from "../types/stores";

/**
 * Category configurations with icons and colors
 */
export const storeCategoryConfigs: Record<
  StoreCategory | "all",
  StoreCategoryConfig
> = {
  all: {
    key: "all",
    label: "All Categories",
    icon: Category,
    gradient: "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
    color: "#1a237e",
  },
  fashion: {
    key: "fashion",
    label: "Fashion & Apparel",
    icon: ShoppingBag,
    gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    color: "#f093fb",
  },
  footwear: {
    key: "footwear",
    label: "Footwear",
    icon: Storefront,
    gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    color: "#4facfe",
  },
  electronics: {
    key: "electronics",
    label: "Electronics",
    icon: StoreIcon,
    gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
    color: "#43e97b",
  },
  food: {
    key: "food",
    label: "Food & Dining",
    icon: Restaurant,
    gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    color: "#fa709a",
  },
  makeup: {
    key: "makeup",
    label: "Beauty & Cosmetics",
    icon: Face,
    gradient: "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
    color: "#ff9a9e",
  },
  jewelry: {
    key: "jewelry",
    label: "Jewelry & Accessories",
    icon: Watch,
    gradient: "linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)",
    color: "#fbc2eb",
  },
  books: {
    key: "books",
    label: "Books & Stationery",
    icon: MenuBook,
    gradient: "linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)",
    color: "#a1c4fd",
  },
  toys: {
    key: "toys",
    label: "Toys & Games",
    icon: Toys,
    gradient: "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
    color: "#ffecd2",
  },
  "home-decor": {
    key: "home-decor",
    label: "Home Decor",
    icon: Home,
    gradient: "linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)",
    color: "#e0c3fc",
  },
  sports: {
    key: "sports",
    label: "Sports & Fitness",
    icon: FitnessCenter,
    gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    color: "#f093fb",
  },
  pharmacy: {
    key: "pharmacy",
    label: "Pharmacy & Health",
    icon: LocalPharmacy,
    gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    color: "#4facfe",
  },
  entertainment: {
    key: "entertainment",
    label: "Entertainment",
    icon: TheaterComedy,
    gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    color: "#fa709a",
  },
  services: {
    key: "services",
    label: "Services",
    icon: Build,
    gradient: "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
    color: "#1a237e",
  },
  others: {
    key: "others",
    label: "Others",
    icon: LocalMall,
    gradient: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
    color: "#a8edea",
  },
};

/**
 * Get category configuration
 */
export const getCategoryConfig = (
  category: StoreCategory | "all",
): StoreCategoryConfig => {
  return storeCategoryConfigs[category] || storeCategoryConfigs.others;
};

/**
 * Format floor display
 */
export const formatFloor = (floor: string): string => {
  if (!floor) return "Unknown Floor";

  // Already formatted (e.g., "Ground Floor", "1st Floor")
  if (floor.toLowerCase().includes("floor")) {
    return floor;
  }

  // Convert short forms (e.g., "G", "1", "2")
  const floorMap: Record<string, string> = {
    g: "Ground Floor",
    gf: "Ground Floor",
    "0": "Ground Floor",
    "1": "1st Floor",
    "2": "2nd Floor",
    "3": "3rd Floor",
  };

  const normalized = floor.toLowerCase().trim();
  if (floorMap[normalized]) {
    return floorMap[normalized];
  }

  // Handle numeric floors (4+)
  const num = parseInt(floor);
  if (!isNaN(num) && num > 3) {
    return `${num}th Floor`;
  }

  return floor;
};

/**
 * Format opening hours for display
 */
export const formatOpeningHours = (hours: any): string => {
  if (!hours) return "Hours not available";

  // If it's a simple string
  if (typeof hours === "string") {
    return hours;
  }

  // If it's an object with days
  const days = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];
  const formattedHours: string[] = [];

  type HoursRange = { days: string[]; hours: string };
  let currentRange: HoursRange | null = null;

  days.forEach((day) => {
    const dayHours = hours[day];
    if (!dayHours) return;

    if (!currentRange || currentRange.hours !== dayHours) {
      if (currentRange) {
        formattedHours.push(
          `${currentRange.days[0]}${
            currentRange.days.length > 1
              ? ` - ${currentRange.days[currentRange.days.length - 1]}`
              : ""
          }: ${currentRange.hours}`,
        );
      }
      currentRange = { days: [day.slice(0, 3)], hours: dayHours };
    } else {
      currentRange.days.push(day.slice(0, 3));
    }
  });

  if (currentRange) {
    // Use type assertion to help TypeScript understand the type after forEach
    const range = currentRange as HoursRange;
    formattedHours.push(
      `${range.days[0]}${
        range.days.length > 1 ? ` - ${range.days[range.days.length - 1]}` : ""
      }: ${range.hours}`,
    );
  }

  return formattedHours.join(", ") || "Hours not available";
};

/**
 * Format phone number
 */
export const formatPhoneNumber = (phone?: string): string => {
  if (!phone) return "";

  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, "");

  // Format as Indian phone number if 10 digits
  if (cleaned.length === 10) {
    return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
  }

  // Return as is if already formatted
  return phone;
};
